// src/components/WorkflowsPage.tsx
//
// Workflow playbooks por org — recetas multi-tool que el agente lee
// cuando un request matchea. CRUD: list + select + edit/create + delete.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Workflow as WorkflowIcon,
  Plus, Save, Trash2, AlertCircle, CheckCircle2,
  Loader2, X, FileText, Tag, Wrench,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';

import {
  listWorkflows, getWorkflowFull, createWorkflow, updateWorkflow, deleteWorkflow,
  type OrgWorkflow, type OrgWorkflowSummary,
} from '@/lib/api';

interface Draft {
  slug: string;
  title: string;
  description: string;
  when_to_use: string;
  steps: string;
  tools_used: string; // comma-separated for the input
  tags: string;
  agent_readable: boolean;
  isNew: boolean;
}

const emptyDraft = (): Draft => ({
  slug: '', title: '', description: '', when_to_use: '', steps: '',
  tools_used: '', tags: '', agent_readable: true, isNew: true,
});

const draftFromWorkflow = (w: OrgWorkflow): Draft => ({
  slug: w.slug, title: w.title, description: w.description,
  when_to_use: w.when_to_use, steps: w.steps,
  tools_used: (w.tools_used || []).join(', '),
  tags: (w.tags || []).join(', '),
  agent_readable: w.agent_readable, isNew: false,
});

const parseList = (s: string): string[] =>
  s.split(',').map((x) => x.trim()).filter(Boolean);

export function WorkflowsPage() {
  const [list, setList] = useState<OrgWorkflowSummary[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const refresh = useCallback(async () => {
    setLoadingList(true); setError('');
    try {
      setList(await listWorkflows());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar la lista');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const loadDetail = async (slug: string) => {
    setLoadingDetail(true); setError(''); setSelected(slug);
    try {
      const w = await getWorkflowFull(slug);
      setDraft(draftFromWorkflow(w));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar');
    } finally {
      setLoadingDetail(false);
    }
  };

  const startNew = () => {
    setSelected(null);
    setDraft(emptyDraft());
    setError('');
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true); setError('');
    try {
      const payload = {
        slug: draft.slug.trim().toLowerCase(),
        title: draft.title.trim(),
        description: draft.description,
        when_to_use: draft.when_to_use,
        steps: draft.steps,
        tools_used: parseList(draft.tools_used),
        tags: parseList(draft.tags),
        agent_readable: draft.agent_readable,
      };
      const saved = draft.isNew
        ? await createWorkflow(payload)
        : await updateWorkflow(draft.slug, payload);
      setDraft(draftFromWorkflow(saved));
      setSelected(saved.slug);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      await refresh();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!draft || draft.isNew) return;
    if (!confirm(`¿Borrar el workflow "${draft.title || draft.slug}"?`)) return;
    setSaving(true); setError('');
    try {
      await deleteWorkflow(draft.slug);
      setSelected(null); setDraft(null);
      await refresh();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'No se pudo borrar');
    } finally {
      setSaving(false);
    }
  };

  const filteredList = useMemo(() => list.slice().sort((a, b) =>
    a.title.localeCompare(b.title)), [list]);

  return (
    <div className="flex h-full min-h-[calc(100vh-7rem)]">
      {/* ── Sidebar: lista de workflows ──────────────── */}
      <aside className="w-72 shrink-0 border-r border-zinc-200 dark:border-zinc-800 overflow-y-auto">
        <div className="p-4 sticky top-0 bg-white dark:bg-zinc-950 border-b border-zinc-200 dark:border-zinc-800 z-10">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-zinc-700 dark:text-zinc-300 flex items-center gap-1.5">
              <WorkflowIcon size={14} /> Workflows
            </h2>
            <button
              onClick={startNew}
              className="flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-zinc-900 text-white hover:bg-zinc-800"
            >
              <Plus size={12} /> Nuevo
            </button>
          </div>
          <p className="text-xs text-zinc-500">
            Recetas multi-tool que el agente sigue cuando un request matchea.
          </p>
        </div>
        {loadingList ? (
          <div className="flex items-center gap-2 p-4 text-zinc-500 text-sm">
            <Loader2 className="animate-spin" size={14} /> Cargando…
          </div>
        ) : filteredList.length === 0 ? (
          <div className="p-6 text-sm text-zinc-500">
            Aún no hay workflows. Crea el primero con <b>Nuevo</b>.
          </div>
        ) : (
          <ul className="p-2">
            {filteredList.map((w) => (
              <li key={w.slug}>
                <button
                  onClick={() => loadDetail(w.slug)}
                  className={`w-full text-left p-2.5 rounded-md transition-colors ${
                    selected === w.slug
                      ? 'bg-zinc-900 text-white'
                      : 'hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-800 dark:text-zinc-200'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm truncate">{w.title}</span>
                    {!w.agent_readable && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                        selected === w.slug ? 'bg-white/20' : 'bg-zinc-200 dark:bg-zinc-700'
                      }`}>
                        oculto al agente
                      </span>
                    )}
                  </div>
                  <div className={`text-[11px] mt-0.5 font-mono ${
                    selected === w.slug ? 'text-white/60' : 'text-zinc-500'
                  }`}>
                    {w.slug}
                  </div>
                  {w.description && (
                    <div className={`text-xs mt-1 line-clamp-2 ${
                      selected === w.slug ? 'text-white/80' : 'text-zinc-600 dark:text-zinc-400'
                    }`}>
                      {w.description}
                    </div>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      {/* ── Detalle / editor ──────────────────────────── */}
      <main className="flex-1 overflow-y-auto">
        {!draft ? (
          <div className="h-full flex items-center justify-center text-zinc-500 text-sm">
            Selecciona un workflow o crea uno nuevo.
          </div>
        ) : (
          <div className="max-w-3xl mx-auto p-6">
            <header className="flex items-start justify-between mb-6 pb-4 border-b border-zinc-200 dark:border-zinc-800">
              <div>
                <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
                  <FileText size={18} />
                  {draft.isNew ? 'Nuevo workflow' : (draft.title || draft.slug)}
                </h1>
                {!draft.isNew && (
                  <p className="text-xs font-mono text-zinc-500 mt-1">{draft.slug}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {saved && (
                  <span className="flex items-center gap-1 text-xs text-emerald-600">
                    <CheckCircle2 size={12} /> Guardado
                  </span>
                )}
                {!draft.isNew && (
                  <button
                    onClick={remove}
                    disabled={saving}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 disabled:opacity-50"
                  >
                    <Trash2 size={14} /> Borrar
                  </button>
                )}
                <button
                  onClick={save}
                  disabled={saving || !draft.title.trim() || !draft.slug.trim()}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                  Guardar
                </button>
              </div>
            </header>

            {error && (
              <div className="mb-4 flex items-start gap-2 p-3 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm dark:bg-rose-950/30 dark:border-rose-900 dark:text-rose-300">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                {error}
              </div>
            )}

            {loadingDetail ? (
              <div className="flex items-center gap-2 text-zinc-500">
                <Loader2 className="animate-spin" size={14} /> Cargando…
              </div>
            ) : (
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Slug" hint="kebab-case, único por org">
                    <input
                      value={draft.slug}
                      onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
                      disabled={!draft.isNew}
                      placeholder="reflexiones-capsula"
                      className="w-full px-3 py-2 text-sm font-mono rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 disabled:bg-zinc-100 dark:disabled:bg-zinc-800 disabled:text-zinc-500"
                    />
                  </Field>
                  <Field label="Título">
                    <input
                      value={draft.title}
                      onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                      placeholder="Producción de cápsula Reflexiones del Infinito"
                      className="w-full px-3 py-2 text-sm rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900"
                    />
                  </Field>
                </div>

                <Field label="Descripción" hint="Una línea — aparece en el sidebar">
                  <input
                    value={draft.description}
                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                    className="w-full px-3 py-2 text-sm rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900"
                  />
                </Field>

                <Field
                  label="Cuándo aplica (when_to_use)"
                  hint="El agente lo lee para matchear el request del usuario. Sé específico — palabras clave, casos típicos."
                >
                  <textarea
                    value={draft.when_to_use}
                    onChange={(e) => setDraft({ ...draft, when_to_use: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 text-sm rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900"
                  />
                </Field>

                <Field label="Steps" hint="Markdown — el agente la lee como receta exacta">
                  <textarea
                    value={draft.steps}
                    onChange={(e) => setDraft({ ...draft, steps: e.target.value })}
                    rows={16}
                    spellCheck={false}
                    className="w-full px-3 py-2 text-sm font-mono leading-relaxed rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100"
                  />
                  {draft.steps && (
                    <details className="mt-2">
                      <summary className="text-xs text-zinc-500 cursor-pointer hover:text-zinc-700 dark:hover:text-zinc-300">Vista previa</summary>
                      <div className="mt-2 p-3 rounded-md bg-zinc-50 dark:bg-zinc-900 prose prose-zinc dark:prose-invert prose-sm max-w-none">
                        <ReactMarkdown>{draft.steps}</ReactMarkdown>
                      </div>
                    </details>
                  )}
                </Field>

                <div className="grid grid-cols-2 gap-4">
                  <Field label={<><Wrench size={12} className="inline mr-1" />Tools used</>} hint="comma-separated">
                    <input
                      value={draft.tools_used}
                      onChange={(e) => setDraft({ ...draft, tools_used: e.target.value })}
                      placeholder="WebSearch, elevenlabs_tts, kling_i2v"
                      className="w-full px-3 py-2 text-sm rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900"
                    />
                  </Field>
                  <Field label={<><Tag size={12} className="inline mr-1" />Tags</>} hint="comma-separated">
                    <input
                      value={draft.tags}
                      onChange={(e) => setDraft({ ...draft, tags: e.target.value })}
                      placeholder="podcast, video, semanal"
                      className="w-full px-3 py-2 text-sm rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900"
                    />
                  </Field>
                </div>

                <label className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={draft.agent_readable}
                    onChange={(e) => setDraft({ ...draft, agent_readable: e.target.checked })}
                  />
                  <span>Visible para el agente (aparece en el manifest del system prompt)</span>
                </label>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}


function Field({
  label, hint, children,
}: { label: React.ReactNode; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">
        {label}
      </label>
      {children}
      {hint && <p className="text-xs text-zinc-500 mt-1">{hint}</p>}
    </div>
  );
}
